import React, { useEffect, useMemo, useState } from "react";
import { get, post } from "../../services/api";
import { MaterialReactTable } from "material-react-table";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import FullScreenLoader from "../../components/FullScreenLoader";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StudentProfile from "../student/student_profile/StudentProfile";

function InspStudentProfile() {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");
  const organizationId = localStorage.getItem("organizationId");
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();

  const [studentsData, setStudentsData] = useState([]);
  const [tablePreferences, setTablePreferences] = useState(null);
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState([]);
  const [columnSizing, setColumnSizing] = useState({});
  const [filterModes, setFilterModes] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [columnLabels, setColumnLabels] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const formatColumnHeader = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/Id$/, " ID")
      .replace(/Name$/, " Name")
      .replace(/Dob/, "Date of Birth");
  };

  const columnsToHide = [
    "id",
    "applyingForSession",
    "classId",
    "entryTermId",
    "genderId",
    "languageId",
    "centerId",
    "isDelete",
    "createdOn",
    "createdBy",
    "updatedOn",
    "updatedBy",
  ];

  const fetchStudents = async () => {
    try {
      if (!centerId || !organizationId) {
        throw new Error("Center ID and Organization ID are required");
      }

      const centerIdNum = parseInt(centerId);
      const organizationIdNum = parseInt(organizationId);

      if (isNaN(centerIdNum) || centerIdNum <= 0) {
        throw new Error("Invalid Center ID");
      }

      if (isNaN(organizationIdNum) || organizationIdNum <= 0) {
        throw new Error("Invalid Organization ID");
      }

      const response = await get(
        `InspStudentFirstRegistrtion/listByCenter?organizationId=${organizationIdNum}&centerId=${centerIdNum}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      let students = [];

      if (response && response.isSuccess !== undefined) {
        if (response.isSuccess) {
          students = response.data || [];
        } else {
          throw new Error(response.message || "Failed to fetch students");
        }
      } else {
        students = Array.isArray(response) ? response : [response];
      }

      const processedData = students.map((student) => ({
        ...student,
        createdBy: userId,
        updatedBy: userId,
        dob: student.dob
          ? new Date(student.dob).toISOString().split("T")[0]
          : "",
        createdOn: student.createdOn
          ? new Date(student.createdOn).toLocaleDateString()
          : "",
        updatedOn: student.updatedOn
          ? new Date(student.updatedOn).toLocaleDateString()
          : "",
      }));

      setStudentsData(processedData);

      if (processedData.length > 0) {
        const firstStudent = processedData[0];
        const labels = {};

        Object.keys(firstStudent).forEach((key) => {
          if (
            !columnsToHide.includes(key) &&
            !["mrt-row-spacer", "actions"].includes(key)
          ) {
            labels[key] = formatColumnHeader(key);
          }
        });

        setColumnLabels(labels);
        setColumnOrder(Object.keys(labels));

        await fetchTablePreferences(labels);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(`Failed to fetch student data: ${error.message}`);
      setStudentsData([]);
      setIsLoading(false);
    }
  };

  const fetchTablePreferences = async (labels = columnLabels) => {
    try {
      if (Object.keys(labels).length === 0) {
        console.warn("No column labels available for table preferences");
        return;
      }

      const response = await get(
        `CommonSettings/GetDatatablePreferences?userId=${userId}&TableName=studentProfileListings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const defaultVisibility = Object.fromEntries(
        Object.keys(labels).map((key) => [key, true])
      );

      const defaultFilterModes = Object.fromEntries(
        Object.keys(labels).map((key) => [
          key,
          key.includes("Id") || key.includes("No") || key.startsWith("is")
            ? "equals"
            : key.includes("Date") || key.includes("On") || key.includes("dob")
            ? "between"
            : "contains",
        ])
      );

      const preferences = {
        columnOrder: response.columnOrder || Object.keys(labels),
        columnVisibility: {
          ...defaultVisibility,
          ...(response.columnVisibility || {}),
        },
        columnSortability: response.columnSortability || {},
        columnSorting: response.columnSorting || [],
        columnFilterability: response.columnFilterability || {},
        columnWidths: Object.fromEntries(
          Object.entries(response.columnWidths || {}).map(([key, value]) => [
            key,
            value || 150,
          ])
        ),
        columnFilterModes: response.columnFilterModes || defaultFilterModes,
      };

      setTablePreferences(preferences);
      setColumnOrder(preferences.columnOrder);
      setColumnVisibility(preferences.columnVisibility);
      setSorting(preferences.columnSorting.slice(0, 1));
      setColumnSizing(preferences.columnWidths);
      setFilterModes(preferences.columnFilterModes);
    } catch (error) {
      console.error("Error fetching table preferences:", error);

      if (Object.keys(labels).length > 0) {
        const defaultVisibility = Object.fromEntries(
          Object.keys(labels).map((key) => [key, true])
        );

        setColumnOrder(Object.keys(labels));
        setColumnVisibility(defaultVisibility);
        setSorting([]);
        setColumnSizing(
          Object.fromEntries(Object.keys(labels).map((key) => [key, 150]))
        );
        setFilterModes(
          Object.fromEntries(
            Object.keys(labels).map((key) => [
              key,
              key.includes("Id") || key.includes("No") || key.startsWith("is")
                ? "equals"
                : key.includes("Date") ||
                  key.includes("On") ||
                  key.includes("dob")
                ? "between"
                : "contains",
            ])
          )
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveTablePreferences = async () => {
    try {
      const filteredColumnSizing = Object.fromEntries(
        Object.entries(columnSizing)
          .filter(([key]) => key !== "mrt-row-spacer" && key !== "actions")
          .map(([key, value]) => [key, Math.max(value, 50)])
      );

      const preferences = {
        columnOrder: columnOrder.filter(
          (key) => key !== "mrt-row-spacer" && key !== "actions"
        ),
        columnVisibility: Object.fromEntries(
          Object.entries(columnVisibility).filter(
            ([key]) => key !== "mrt-row-spacer" && key !== "actions"
          )
        ),
        columnSortability: tablePreferences?.columnSortability || {},
        columnSorting: sorting
          .filter((s) => s.id !== "actions")
          .map((s) => ({
            id: s.id,
            desc: s.desc === "True" || s.desc === true,
          })),
        columnFilterability: tablePreferences?.columnFilterability || {},
        columnWidths: filteredColumnSizing,
        columnFilterModes: Object.fromEntries(
          Object.entries(filterModes).filter(
            ([key]) => key !== "mrt-row-spacer" && key !== "actions"
          )
        ),
      };

      const payload = {
        userId,
        tableId: "studentProfileListings",
        preferences,
      };

      const response = await post(
        `CommonSettings/SaveDatatableFiters`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success("Preferences saved successfully");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error saving preferences:", error);
      toast.error(`Failed to save preferences: ${error.message}`);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchStudents();
      setLoading(false);
    };

    fetchData();
  }, []);

  const handleEditProfile = (student) => {
    setSelectedStudent(student);
    setIsProfileModalOpen(true);
  };

  const columns = useMemo(() => {
    if (Object.keys(columnLabels).length === 0) {
      return [];
    }

    const keysToUse =
      columnOrder.length > 0 ? columnOrder : Object.keys(columnLabels);

    return [
      ...keysToUse
        .filter(
          (key) =>
            key !== "mrt-row-spacer" &&
            key !== "actions" &&
            columnLabels[key] &&
            !columnsToHide.includes(key)
        )
        .map((key) => ({
          accessorKey: key,
          id: key,
          header: columnLabels[key] || formatColumnHeader(key),
          filterVariant: key.includes("is") ? "checkbox" : "text",
          enableColumnFilter: true,
          enableColumnFilterModes: true,
          filterModeOptions: [
            "contains",
            "startsWith",
            "endsWith",
            "equals",
            "notEquals",
            "greaterThan",
            "lessThan",
            "greaterThanOrEqualTo",
            "lessThanOrEqualTo",
            "between",
            "empty",
            "notEmpty",
          ],
          filterFn:
            filterModes[key] ||
            (key.includes("Id") || key.includes("No") || key.startsWith("is")
              ? "equals"
              : key.includes("Date") ||
                key.includes("On") ||
                key.includes("dob")
              ? "between"
              : "contains"),
          enableSorting: true,
          size: columnSizing[key] || 150,
          minSize: 50,
          maxSize: 1000,
        })),
      {
        accessorKey: "actions",
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        enablePinning: true,
        pinned: "right",
        size: 100,
        Cell: ({ row }) => (
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Edit Profile"
              onClick={(e) => {
                e.stopPropagation();
                handleEditProfile(row.original);
              }}
            >
              <i className="text-primary fs-8 fas fa-edit"></i>
            </button>
          </div>
        ),
      },
    ];
  }, [columnOrder, columnLabels, columnSizing, filterModes]);

  const exportToExcel = () => {
    if (studentsData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const exportData = studentsData.map((student) => {
      const exportStudent = {};
      Object.keys(student).forEach((key) => {
        if (!columnsToHide.includes(key) && key !== "isDelete") {
          exportStudent[key] = student[key];
        }
      });
      return exportStudent;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Student Profiles");
    XLSX.writeFile(workbook, "student_profiles_data.xlsx");
  };

  const exportToPDF = () => {
    if (studentsData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const doc = new jsPDF("landscape", "pt", "A4");

    const columns = Object.keys(columnLabels)
      .filter((key) => !columnsToHide.includes(key) && key !== "isDelete")
      .map((key) => ({
        header: columnLabels[key] || formatColumnHeader(key),
        dataKey: key,
      }));

    const rows = studentsData.map((row) => {
      const filteredRow = {};
      columns.forEach((col) => {
        filteredRow[col.dataKey] = row[col.dataKey] ?? "";
      });
      return filteredRow;
    });

    doc.setFontSize(14);
    doc.text("Student Profiles Data", 40, 40);

    doc.autoTable({
      columns,
      body: rows,
      startY: 60,
      theme: "striped",
      styles: {
        fontSize: 8,
        cellPadding: 4,
        overflow: "linebreak",
        minCellHeight: 20,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        halign: "center",
      },
      bodyStyles: {
        halign: "left",
        valign: "middle",
      },
      margin: { top: 60, left: 40, right: 40 },
      tableWidth: "wrap",
    });

    doc.save("student_profiles_data.pdf");
  };

  return (
    <>
      <div className="page-header d-flex mb-3">
        <h2 className="page-heading fs-7">Student Profiles</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="custom-mrt-table-wrapper">
            {isLoading ? (
              <div className="text-center p-4">Loading...</div>
            ) : studentsData.length > 0 && columns.length > 0 ? (
              <MaterialReactTable
                columns={columns}
                data={studentsData}
                enableColumnFilters
                enableGlobalFilter
                enableColumnDragging
                enableColumnResizing
                enableHiding
                enableColumnOrdering
                enableSorting
                enableStickyHeader
                enableColumnFilterModes
                defaultColumn={{
                  minSize: 50,
                  maxSize: 1000,
                  size: 150,
                }}
                initialState={{
                  showColumnFilters: true,
                  columnOrder: [
                    ...Object.keys(columnLabels).filter(
                      (key) =>
                        !columnsToHide.includes(key) && key !== "isDelete"
                    ),
                    "actions",
                  ],
                  columnVisibility,
                  sorting,
                  columnSizing,
                  columnFilters,
                  density: "compact",
                  columnPinning: { right: ["actions"] },
                }}
                state={{
                  columnOrder,
                  columnVisibility,
                  sorting,
                  columnSizing,
                  columnFilters,
                }}
                onColumnOrderChange={setColumnOrder}
                onColumnVisibilityChange={setColumnVisibility}
                onSortingChange={setSorting}
                onColumnSizingChange={setColumnSizing}
                onColumnFiltersChange={setColumnFilters}
                muiTableBodyRowProps={({ row }) => ({
                  sx: { cursor: "pointer" },
                })}
                muiTableProps={{
                  className: "custom-mrt-table",
                }}
                muiTableHeadCellProps={{
                  className: "custom-mrt-table-head-cell",
                }}
                muiTableBodyCellProps={{
                  className: "custom-mrt-table-body-cell",
                }}
                renderTopToolbarCustomActions={() => (
                  <div className="d-flex gap-1 align-items-center">
                    <button
                      type="button"
                      className="btn btn-with-icon-and-tooltip"
                      data-tooltip-id="global-tooltip"
                      data-tooltip-content="Save Preferences"
                      onClick={saveTablePreferences}
                    >
                      <i className="text-primary fs-7 fas fa-save"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-with-icon-and-tooltip"
                      data-tooltip-id="global-tooltip"
                      data-tooltip-content="Export to Excel"
                      onClick={exportToExcel}
                    >
                      <i className="text-success fs-7 fas fa-file-excel"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-with-icon-and-tooltip"
                      data-tooltip-id="global-tooltip"
                      data-tooltip-content="Export to PDF"
                      onClick={exportToPDF}
                    >
                      <i className="text-danger fs-7 fas fa-file-pdf"></i>
                    </button>
                  </div>
                )}
              />
            ) : (
              <div className="text-center p-4">
                {studentsData.length === 0
                  ? "No student data available"
                  : "Loading columns..."}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Student Profile
          <IconButton
            aria-label="close"
            onClick={() => setIsProfileModalOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <StudentProfile
            student={selectedStudent}
            onClose={() => setIsProfileModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {loading && <FullScreenLoader />}
    </>
  );
}

export default InspStudentProfile;
